import { useEffect, useState } from 'react'

/**
 * Which engagement dialog is open right now (#25610), so a page-view announcement and a survey
 * never open on top of each other. `AnnouncementCenter` and `SurveyCenter` are siblings with no
 * shared state but the Apollo cache, and the cache cannot say "on screen now".
 *
 * Set it in the same effect that opens the dialog, not in a later effect: the sibling's effect
 * runs in the same commit and must already see it.
 */
export type EngagementSurface = 'announcement' | 'survey'

const open = new Set<EngagementSurface>()
const listeners = new Set<() => void>()

export function setSurfaceOpen(surface: EngagementSurface, isOpen: boolean): void {
  if (open.has(surface) === isOpen) return
  if (isOpen) open.add(surface)
  else open.delete(surface)
  listeners.forEach((listener) => listener())
}

export function isSurfaceOpen(surface: EngagementSurface): boolean {
  return open.has(surface)
}

export function subscribeSurfaces(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Re-renders when `surface` opens or closes. */
export function useSurfaceOpen(surface: EngagementSurface): boolean {
  const [value, setValue] = useState(() => isSurfaceOpen(surface))
  useEffect(() => {
    setValue(isSurfaceOpen(surface))
    return subscribeSurfaces(() => setValue(isSurfaceOpen(surface)))
  }, [surface])
  return value
}
